<template>
  <div v-if="canRead">
    <div class="card col-md-12">
      <div class="card-header" style="margin-bottom: 10px">
        <h4 class="card-title" style="display:inline">
          City Search
        </h4>
        <button type="button" v-if="canEdit" @click="addCity" class="btn btn-fill btn-info btn-add-company pull-right">
          Add City
        </button>
      </div>
      <div class="card-body form-group row">
        <div class="col-md-12">
          <input type="search" name="search" placeholder="Search by City, County or Zip" v-validate="'required'" class="form-control" @keyup.enter="search" v-model="query">
          <small class="text-danger" v-show="errors.has('search')">
            {{ getError('search') }}
          </small>
        </div>
        <div class="col-md-12">
          <button type="button" @click="search" :disabled="isSearchButtonDisabled" class="btn btn-fill btn-info pull-right btn-search">
            Search
          </button>
          <button type="button" @click="clear" :disabled="isSearchButtonDisabled" class="btn btn-fill btn-info pull-right btn-search">
            Clear
          </button>
        </div>
      </div>
    </div><!-- end card -->
    <div class="card">
      <div class="card-body">
        <div class="col-md-12">
          <el-table class="wrap-whole-words indexed-table-column-width"
            :data="tableData"
            header-row-class-name="text-primary"
            :max-height="tableHeight(300)">
            <el-table-column
              :width="this.constants.INDEX_COLUMN_WIDTHS.WIDE"
              align="center"
              label="#"
              type="index">
            </el-table-column>
            <el-table-column label="City" width="140">
              <template slot-scope="props">
                <span class="invisible">{{ props.row.id }}</span>
                <a href="#" @click.prevent="editCity(props.row)">{{ props.row.name }}</a>
              </template>
            </el-table-column>
            <el-table-column label="State">
              <template slot-scope="props">
                {{ props.row.state }}
              </template>
            </el-table-column>
            <el-table-column label="Counties">
              <template slot-scope="props">
                {{ props.row.counties | toCsv }}
              </template>
            </el-table-column>
            <el-table-column label="Zip Codes">
              <template slot-scope="props">
                {{ props.row.zipCodes | toCsv }}
              </template>
            </el-table-column>
            <el-table-column
              width="105"
              fixed="right"
              label="Actions">
              <template slot-scope="props">
                <p-button type="success" size="sm" icon>
                  <i class="fa fa-edit" @click="editCity(props.row)"></i>
                </p-button>
                <p-button type="danger" v-if="canEdit" size="sm" icon @click="confirmDelete(props.row)">
                  <i class="fa fa-times"></i>
                </p-button>
              </template>
            </el-table-column>
          </el-table>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
  import {mapGetters} from 'vuex'
  import _ from 'lodash'
  import {notifySuccess, notifyError, messageForSafeDelete, getTableHeight, notifyWarning} from 'src/utilities/utils'
  import Constants from 'src/utilities/constants'
  import Permissions from 'src/utilities/permissions'
  import PButton from 'src/components/UIComponents/Button.vue'
  import {NO_CONTENT, OK, UNAUTHORIZED} from 'http-status-codes'

  export default {
    components: {
      PButton
    },
    computed: {
      ...mapGetters({
        tableData: 'cities/results',
        states: 'states'
      }),
      isSearchButtonDisabled() {
        return !this.query
      },
      canRead() {
        return this.$can('read', Permissions.CITIES)
      },
      canEdit() {
        return this.$can('edit', Permissions.CITIES)
      }
    },
    data() {
      return {
        toDelete: null,
        query: null,
        constants: Constants
      }
    },
    methods: {
      clear() {
        this.$store.commit('cities/resetResults')
        this.$validator.pause()
        this.query = null
        setTimeout(this.resumeValidation, 250)
      },
      resumeValidation() {
        this.$validator.resume()
      },
      tableHeight(buffer) {
        return getTableHeight(buffer)
      },
      getError(fieldName) {
        return this.errors.first(fieldName)
      },
      addCity() {
        this.$eventHub.$emit('show-modal-1', 'City', null, '700px')
      },
      async editCity(city) {
        this.$eventHub.$emit('show-modal-1', 'City', {id: city.id}, '700px')
      },
      async search(optionalCity) {
        let isSearching = true // refreshing search results
        let query = _.trim(this.query)
        if (optionalCity.hasOwnProperty('name')) {
          query = optionalCity.name
          isSearching = false
        }
        // don't fire validation when reloading after adding/editing a street name
        if (!isSearching || (isSearching && await this.$validator.validateAll())) {
          const response = await this.$store.dispatch('cities/fetchByNameOrCountyOrZip', query)
          if (OK === response.status && response.data.length === 0) {
            this.$eventHub.$emit('show-modal-1', 'No Results Found', null, '500px')
          }
        }
      },
      async confirmDelete(city) {
        this.toDelete = city.id
        let showButton = false

        const responseAddress = await this.$store.dispatch('projects/fetchExistsByAddressCity', city)
        if (OK !== responseAddress.status) {
          notifyError(this, 'Error determining if ' + city + ' is in use. Unable to delete city.')
          return
        }

        if (responseAddress.data){
          const message = 'You can not delete <strong>' + city.name + '</strong> as it is used by at least one project address. You may inactivate it.'
          this.showConfirmDelete(city, message, showButton)
          return
        }

        let message = 'Are you sure you want to delete <strong>' + city.name + '</strong>? This will completely remove the city.'

        const responseStreets = await this.$store.dispatch('streets/fetchFindByCity', city.name)

        if (OK !== responseStreets.status) {
          notifyError(this, 'Error determining if ' + city + ' is in use. Unable to delete city.')
          return
        }

        const usedByStreets = responseStreets.data
        if (usedByStreets.length > 0) {
          message = '<strong>' + city.name + '</strong> is used by the following streets and cannot be deleted:'
          message += '<p>'
          message = messageForSafeDelete(usedByStreets, 'name', message)
          message += '</p>'
          message += '<p>If you really want to delete this city, you must first remove associations to it in each of the streets listed above, then come back here and delete the city.</p>'
          showButton = false
          this.showConfirmDelete(city, message, showButton)
          return
        }

        const responsePriceRules = await this.$store.dispatch('priceRules/fetchFindByCity', city.name)

        if (OK !== responsePriceRules.status) {
          notifyError(this, 'Error determining if ' + city + ' is in use. Unable to delete city.')
          return
        }

        const usedByPriceRules = responsePriceRules.data
        if (usedByPriceRules.length > 0) {
          message = '<strong>' + city.name + '</strong> is used by the following price rules and cannot be deleted:'
          message += '<p>'
          message = messageForSafeDelete(usedByPriceRules, 'shortId', message)
          message += '</p>'
          message += '<p>If you really want to delete this city, you must first remove associations to it in each of the price rules listed above, then come back here and delete the city.</p>'
          showButton = false
          this.showConfirmDelete(city, message, showButton)
          return
        }

        // happy path - city can be deleted
        showButton = true
        this.showConfirmDelete(city, message, showButton)
      },
      showConfirmDelete(city, message, showButton) {
        const payload = {
          message,
          entity: city,
          delete: this.delete,
          showButton
        }
        this.$eventHub.$emit('show-modal-1', 'Confirm Delete', payload, '600px')
      },
      async delete() {
        const response = await this.$store.dispatch('delete', {
          endpoint: Constants.ENTITY_ENDPOINTS.CITIES,
          id: this.toDelete
        })

        if (NO_CONTENT === response.status) {
          notifySuccess(this, 'City Deleted Successfully')
          this.$store.commit('cities/delete', this.toDelete)
        } else if (UNAUTHORIZED === response.status) {
          notifyWarning(this, 'You are not authorized to delete Cities')
        } else {
          notifyError(this, 'Error Deleting City')
        }
      }
    },
    mounted () {
      this.$eventHub.$on('citySaved', this.search)
    },
    destroyed () {
      this.$eventHub.$off('citySaved')
    }
  }
</script>
<style>
  .btn-add-company {
    margin-left: 15px;
  }
  .btn-search {
    margin-bottom: 15px;
   }
  .results-per-page {
    margin-bottom: 5px;
  }
</style>
